import type {
  OrderItemFulfilmentDetails,
  OrderStockShortage,
} from '@skupreme/api-contracts/core';
import {
  getProviderAvailableForOrderItem,
  getUnfulfilledOrderItemQty,
  getUnfulfillablePendingShipmentItemCount,
  isProviderStockKnown,
} from './availability';
import type { OrderIssue } from './issues';

/* Only the fields the summary touches: the list screen types its rows loosely. */
export type FulfillmentOrderItem = {
  item_id?: string;
  _id?: string;
  title?: string;
  sku?: string;
  qty?: number;
  state?: string;
  fulfilment_details?: OrderItemFulfilmentDetails[];
};

export type FulfillmentProvider = {
  _id?: string;
  name?: string;
  integration_type?: string;
  address?: { city?: string; state?: string };
  wmsOrganizationLogo?: { url?: string };
  items?: { _id?: string; item_id?: string; available?: number }[];
};

export const PENDING_SHIPMENT = 'Pending Shipment';

const PROVIDER_LOGOS: Record<string, string> = {
  manual: '/images/fp/manual.svg',
  spapi: '/images/fp/fba.png',
  deliverr: '/images/fp/deliverr.png',
  shiphero: '/images/fp/shiphero.png',
  '3pl': '/images/fp/3pl-grey.svg',
  tactical3pl: '/images/fp/tactical.svg',
};

/** The logo for a provider's integration; a generic 3PL mark when unknown. */
export function getProviderLogo(integrationType: string | undefined): string {
  return PROVIDER_LOGOS[integrationType || ''] || PROVIDER_LOGOS['3pl'];
}

export type FulfillmentLine = {
  key: string;
  itemId?: string;
  title: string;
  sku: string;
  state?: string;
  qty: number;
  isShort: boolean;
  /** What the chosen provider holds. Only meaningful alongside `isShort`. */
  available: number;
};

export type FulfillmentSummaryOptions = {
  /** Only the pending-shipment queue asks whether stock is there today. */
  highlightShortages?: boolean;
};

export type FulfillmentSummary = {
  /** The provider the order will ship from, when there is a single one. */
  provider?: FulfillmentProvider;
  providers: FulfillmentProvider[];
  isSplit: boolean;
  /** Distinct products, and the units across them. */
  lineCount: number;
  unitCount: number;
  lines: FulfillmentLine[];
};

/** The provider the order ships through, or none when it is split. */
export function getCurrentProvider(
  providers: FulfillmentProvider[] | undefined,
  selectedFP: FulfillmentProvider | undefined,
): FulfillmentProvider | undefined {
  const list = providers || [];

  if (list.length !== 1) {
    return undefined;
  }

  return selectedFP?._id && list[0]._id !== selectedFP._id
    ? selectedFP
    : list[0];
}

/**
 * Everything the fulfillment column shows: who ships the order, what is in it,
 * and which lines the chosen provider cannot cover.
 */
export function getFulfillmentSummary(
  items: FulfillmentOrderItem[] | undefined,
  providers: FulfillmentProvider[] | undefined,
  selectedFP?: FulfillmentProvider,
  { highlightShortages = false }: FulfillmentSummaryOptions = {},
): FulfillmentSummary {
  const list = providers || [];
  const provider = getCurrentProvider(list, selectedFP);
  const providerItems = provider?.items || [];
  const stockKnown = isProviderStockKnown(provider);

  const lines = (items || []).map((item, index) => {
    const remaining = getUnfulfilledOrderItemQty(item);
    const available = getProviderAvailableForOrderItem(providerItems, item);

    return {
      key: item.item_id || item._id || `${index}`,
      itemId: item.item_id || item._id,
      title: item.title || item.sku || 'Item',
      sku: item.sku || '',
      state: item.state,
      qty: item.qty || 0,
      isShort:
        highlightShortages &&
        stockKnown &&
        item.state === PENDING_SHIPMENT &&
        available < remaining,
      available,
    };
  });

  return {
    provider,
    providers: list,
    isSplit: list.length > 1,
    lineCount: lines.length,
    unitCount: lines.reduce((total, line) => total + line.qty, 0),
    lines,
  };
}

export type FulfillmentIssueInput = {
  items: FulfillmentOrderItem[] | undefined;
  providers: FulfillmentProvider[] | undefined;
  selectedFP?: FulfillmentProvider;
  orderState?: string;
  isShipped?: boolean;
  /** Only the pending-shipment queue asks whether stock is there today. */
  highlightShortages?: boolean;
  /** V2 rows: the API's answer (`stock_shortage`), used instead of judging stock here; legacy rows leave it off. */
  stockShortage?: OrderStockShortage | null;
};

/** The out-of-stock issue for `short` of `total` lines, as the list row and the V2 shipment card both say it. */
export function stockShortageIssue(short: number, total: number): OrderIssue {
  return {
    key: 'out-of-stock',
    kind: 'inventory',
    reason:
      short === total
        ? 'All items out of stock'
        : `${short} / ${total} items out of stock`,
    detail:
      'The selected fulfillment provider does not hold enough stock to ship these lines.',
    hint: 'Add stock at the provider, or change the provider on the order.',
  };
}

/** An unassigned provider and missing stock, as entries for the issue list. */
export function getFulfillmentIssues({
  items,
  providers,
  selectedFP,
  orderState,
  isShipped,
  highlightShortages,
  stockShortage,
}: FulfillmentIssueInput): OrderIssue[] {
  if (isShipped) {
    return [];
  }

  const list = providers || [];

  if (list.length === 0 && orderState !== PENDING_SHIPMENT) {
    return [
      {
        key: 'provider-unassigned',
        kind: 'provider',
        reason: 'Provider Unassigned',
        detail: 'No fulfillment provider has been chosen for this order yet.',
      },
    ];
  }

  /* The API judged a V2 order's lines at the provider each is routed to, and only while it is Unfulfilled. */
  if (stockShortage !== undefined) {
    return stockShortage
      ? [
          stockShortageIssue(
            stockShortage.short_line_ids.length,
            stockShortage.line_count,
          ),
        ]
      : [];
  }

  /* Stock is judged against one provider's shelves, so it needs exactly one. */
  if (list.length !== 1 || !highlightShortages) {
    return [];
  }

  const provider = getCurrentProvider(list, selectedFP);
  const short = getUnfulfillablePendingShipmentItemCount(items, provider);

  if (short === 0) {
    return [];
  }

  /* `short` only judges the lines still awaiting shipment, so the ratio must too. */
  const total = (items || []).filter(
    item => item.state === PENDING_SHIPMENT,
  ).length;

  return [stockShortageIssue(short, total)];
}
